import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import StatCard from '../../../../shared/layout/cards/StatCard';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import { MdTrendingUp, MdTrendingDown, MdStore, MdSyncAlt } from 'react-icons/md';
import { fetchDistributorManufacturerDetail } from '../../../../services/Http/DistributorApi';
import { useCurrency } from '../../../../context/CurrencyProvider';

const detailMetrics = [
    { key: 'inbound_value', label: 'DistributorManufacturerDetail.metrics.inboundValue', icon: MdTrendingDown, format: 'currency', color: 'revenueValue' },
    { key: 'outbound_value', label: 'DistributorManufacturerDetail.metrics.outboundValue', icon: MdTrendingUp, format: 'currency', color: 'totalVolume' },
    { key: 'inbound_quantity', label: 'DistributorManufacturerDetail.metrics.inboundQuantity', icon: MdSyncAlt, format: 'number', color: 'transactions' },
    { key: 'outbound_quantity', label: 'DistributorManufacturerDetail.metrics.outboundQuantity', icon: MdSyncAlt, format: 'number', color: 'transactions' },
    { key: 'active_retailers', label: 'DistributorManufacturerDetail.metrics.activeRetailers', icon: MdStore, format: 'number', color: 'retailers' },
];

const DistributorManufacturerDetail = () => {
    const { manufacturerId } = useParams();
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const formatAmount = useCallback((value, localAmount = null, localCurrencyId = null, localCurrencyCode = null) => {
        return formatResolvedAmount({
            eurAmount: value,
            localAmount,
            localCurrencyId,
            localCurrencyCode,
        });
    }, [formatResolvedAmount]);

    useEffect(() => {
        const loadDetail = async () => {
            try {
                const response = await fetchDistributorManufacturerDetail(manufacturerId);
                setData(response);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadDetail();
    }, [manufacturerId]);

    if (loading) {
        return <LoadingMessage>{t('DistributorManufacturerDetail.loading')}</LoadingMessage>;
    }

    if (error || !data) {
        return <ErrorMessage>{t('DistributorManufacturerDetail.error')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <div>
                    <PageTitle>{data.manufacturer.name}</PageTitle>
                    <Subtitle>{data.manufacturer.description || t('DistributorManufacturerDetail.subtitle')}</Subtitle>
                </div>
                <BackLink to="/manufacturers">{t('DistributorManufacturerDetail.back')}</BackLink>
            </PageHeader>
            <PageContent>
                <StatsGrid>
                    {detailMetrics.map(({ key, label, icon: Icon, format, color }) => (
                        <StatCard
                            key={key}
                            icon={<Icon className="icon" />}
                            title={t(label)}
                            value={formatValue({
                                value: data.metrics[key],
                                localAmount: key === 'inbound_value' ? data.metrics.inbound_value_local : key === 'outbound_value' ? data.metrics.outbound_value_local : null,
                                localCurrencyId: key === 'inbound_value' ? data.metrics.inbound_currency_id : key === 'outbound_value' ? data.metrics.outbound_currency_id : null,
                            }, format, formatAmount)}
                            color={color}
                        />
                    ))}
                </StatsGrid>

                <Panels>
                    <Panel>
                        <PanelTitle>{t('DistributorManufacturerDetail.topRetailers')}</PanelTitle>
                        {renderList(data.top_retailers, t, formatAmount)}
                    </Panel>
                    <Panel>
                        <PanelTitle>{t('DistributorManufacturerDetail.recentActivity')}</PanelTitle>
                        {renderActivity(data.recent_activity, t, formatAmount)}
                    </Panel>
                </Panels>
            </PageContent>
        </PageContainer>
    );
};

const formatValue = (value, formatType = 'number', formatAmount = (v) => v) => {
    const metric = typeof value === 'object' && value !== null ? value : { value };
    const rawValue = metric.value;

    if (rawValue === null || rawValue === undefined) {
        return '0';
    }
    const numeric = Number(rawValue);
    if (Number.isNaN(numeric)) {
        return rawValue;
    }
    if (formatType === 'currency') {
        return formatAmount(
            numeric,
            metric.localAmount,
            metric.localCurrencyId,
            metric.localCurrencyCode
        );
    }
    return numeric.toLocaleString();
};

const renderList = (items, t, formatAmount) => {
    if (!items || items.length === 0) {
        return <EmptyState>{t('DistributorManufacturerDetail.empty')}</EmptyState>;
    }
    return (
        <List>
            {items.map((item, idx) => (
                <li key={`retailer-${idx}`}>
                    <span>{item.retailer}</span>
                    <strong>{formatValue({
                        value: item.value,
                        localAmount: item.value_local,
                        localCurrencyId: item.seller_currency_id,
                    }, 'currency', formatAmount)}</strong>
                </li>
            ))}
        </List>
    );
};

const renderActivity = (items, t, formatAmount) => {
    if (!items || items.length === 0) {
        return <EmptyState>{t('DistributorManufacturerDetail.empty')}</EmptyState>;
    }
    return (
        <ActivityList>
            {items.map((item, idx) => (
                <li key={`activity-${idx}`}>
                    <div>
                        <strong>{item.direction === 'outbound' ? t('DistributorManufacturerDetail.activity.outbound') : t('DistributorManufacturerDetail.activity.inbound')}</strong>
                        <small>{item.date}</small>
                    </div>
                    <span>{formatValue({
                        value: item.value,
                        localAmount: item.direction === 'outbound' ? item.outbound_value_local : item.inbound_value_local,
                        localCurrencyId: item.direction === 'outbound' ? item.outbound_currency_id : item.inbound_currency_id,
                    }, 'currency', formatAmount)}</span>
                </li>
            ))}
        </ActivityList>
    );
};

const Subtitle = styled.p`
    margin: 0.25rem 0 0;
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
`;

const BackLink = styled(Link)`
    color: #821692;
    font-weight: 600;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const Panels = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.25rem;
`;

const Panel = styled.div`
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    padding: 1.25rem;
`;

const PanelTitle = styled.h4`
    margin-top: 0;
`;

const List = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        display: flex;
        justify-content: space-between;
        padding: 0.625rem 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    li:last-child {
        border-bottom: none;
    }
`;

const ActivityList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        display: flex;
        justify-content: space-between;
        padding: 0.625rem 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    li:last-child {
        border-bottom: none;
    }

    small {
        display: block;
        color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
    }
`;

const EmptyState = styled.p`
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
`;

export default DistributorManufacturerDetail;
